// In-flight card-tender tracking. A marker is written the moment a PIN-pad charge is started and
// cleared when its result is DEFINITIVE (approved into the cart, canceled, or declined). If the
// browser dies mid-charge, the marker survives; on the next register load we reconcile it against
// Payroc by its (unique) terminal order id. A charge found for an order that was never finalized is
// an orphan — surfaced to the cashier to reconcile (the cart is gone, so it can't be auto-rebuilt).

import { auth, nodeReconcileTerminalPayment } from "./api";

const KEY = (mid) => `qv_web_register_inflight_${mid || ""}`;
const GRACE_MS = 90 * 1000; // don't reconcile a tender a still-open tab may be handling
const MAX = 20;

export function listInFlight() {
  try {
    const raw = localStorage.getItem(KEY(auth().merchantId));
    const a = raw ? JSON.parse(raw) : [];
    return Array.isArray(a) ? a : [];
  } catch {
    return [];
  }
}

function persist(list) {
  try {
    localStorage.setItem(KEY(auth().merchantId), JSON.stringify(list.slice(0, MAX)));
  } catch {
    /* quota / disabled */
  }
}

// rec: { terminalOrderId, cardType, amount, idempotencyKey }
export function addInFlight(rec) {
  if (!rec || !rec.terminalOrderId) return;
  const list = listInFlight().filter((r) => r.terminalOrderId !== rec.terminalOrderId);
  list.unshift({ ...rec, at: Date.now() });
  persist(list);
}

export function removeInFlight(terminalOrderId) {
  if (!terminalOrderId) return;
  persist(listInFlight().filter((r) => r.terminalOrderId !== terminalOrderId));
}

/**
 * Reconcile leftover in-flight tenders on register load. Returns the list of RECOVERED charges
 * (approved on Payroc but never finalized) so the UI can alert the cashier. Definitively-unpaid or
 * confirmed markers are cleared; markers that error out (network) are left for the next load.
 */
export async function reconcileInFlight() {
  const recovered = [];
  for (const r of listInFlight()) {
    if (Date.now() - (r.at || 0) < GRACE_MS) continue; // give a live tab time to resolve it
    try {
      const res = await nodeReconcileTerminalPayment({ orderId: r.terminalOrderId });
      if (res && res.status && res.found && res.approved) {
        recovered.push({ ...r, paymentId: res.payment_id, cardBrand: res.card_type, authorizedAmount: res.authorized_amount });
        removeInFlight(r.terminalOrderId); // acknowledged via the alert; don't re-alert every load
      } else if (res && res.status && res.found === false) {
        removeInFlight(r.terminalOrderId); // definitively no charge → safe to drop
      }
      // reconcile error / not conclusive → leave for the next load
    } catch {
      /* leave for retry */
    }
  }
  return recovered;
}
